import { useMemo } from 'react'
import { useHistory } from 'react-router-dom'
import { client } from '../../hooks/Client'
import useMsiResearchDataset from '../../hooks/useMsiResearchDataset'
import cronbachAlpha from '../../helpers/analysisHelpers/cronbachAlpha'
import factorAnalysis, { buildItemCorrelationMatrix } from '../../helpers/analysisHelpers/factorAnalysis'
import { effectScoreMatrix } from '../../helpers/analysisHelpers/buildMsiResearchDataset'
import exportMsiResultsXlsx from '../../helpers/analysisHelpers/exportMsiResultsXlsx'
import { getSectorInfo } from '../../helpers/meetstandaard/sectorCatalog'

// Vanaf deze absolute lading rekent een item "bij" een factor — gangbare
// vuistregel in exploratieve factoranalyse.
const LOADING_HIGHLIGHT = 0.4

const formatStat = (value, decimals = 2) =>
    (value === null || value === undefined ? '—' : value.toFixed(decimals))

/**
 * Onderzoeksdata-sectie (superTenant) — Cronbach's alpha per MSI-effect en
 * een exploratieve factoranalyse over alle items, op data van alle
 * organisaties. De resultaten-download bevat ook de volledige
 * gepseudonimiseerde dataset (tabblad 'Data'); per datapunt is de data
 * daarnaast via een doorklik in te zien.
 *
 * @param {object} props
 * @param {string} props.sector - Sector-slug uit de querystring; '' = hele meetstandaard
 */
const MsiResearchDataSection = ({ sector }) => {
    const history = useHistory()
    const { dataset, effects, effectErrors, loading, error } = useMsiResearchDataset(sector)

    // Alpha per effect — alleen effecten die in de dataset voorkomen
    const alphaRows = useMemo(() => {
        if (!dataset) return []
        return effects
            .filter(({ msiId }) => dataset.itemKeysByEffect[msiId])
            .map(({ msiId, name }) => {
                const { alpha, n, k } = cronbachAlpha(effectScoreMatrix(dataset, msiId))
                return { msiId, name, alpha, n, k }
            })
    }, [dataset, effects])

    // Factoranalyse over ALLE items samen (Kaiser-criterium voor het aantal factoren)
    const factors = useMemo(() => {
        if (!dataset || dataset.columns.length < 2 || dataset.rows.length < 3) return null
        const itemKeys = dataset.columns.map((column) => column.key)
        const { matrix } = buildItemCorrelationMatrix(dataset.rows, itemKeys)
        return factorAnalysis(matrix)
    }, [dataset])

    const summary = useMemo(() => {
        if (!dataset) return null
        const orgs = new Set(dataset.rows.map((row) => row.orgName))
        const respondents = new Set(dataset.rows.map((row) => row.respondentLabel))
        return {
            effectCount: Object.keys(dataset.itemKeysByEffect).length,
            itemCount: dataset.columns.length,
            orgCount: orgs.size,
            respondentCount: respondents.size,
        }
    }, [dataset])

    // Zonder argumenten: de volledige dataset. Een datapunt in de analyse
    // (effect-rij in de alpha-tabel, item-rij in de factoranalyse) geeft een
    // filter mee zodat de dataweergave opent op precies dat datapunt.
    const openDataTable = ({ effect = '', item = '' } = {}) => {
        const params = new URLSearchParams()
        if (sector) params.set('sector', sector)
        if (item) params.set('item', item)
        else if (effect) params.set('effect', effect)
        const query = params.toString()
        history.push(`/${client}/deepanalysis/meetstandaarddata${query ? `?${query}` : ''}`)
    }

    if (error) return <p className='deep-analysis-error'>{error}</p>
    if (loading) return <p>Onderzoeksdata wordt geladen — één berekening per effect, dit kan even duren…</p>

    const hasData = dataset && dataset.rows.length > 0

    return (
        <>
            {Object.keys(effectErrors).length > 0 && (
                <div className='deep-analysis-section'>
                    {Object.entries(effectErrors).map(([msiId, message]) => {
                        const effect = effects.find((e) => e.msiId === msiId)
                        return (
                            <p key={msiId} className='deep-analysis-error'>
                                Effect &quot;{effect?.name || msiId}&quot; kon niet worden geladen: {message}
                            </p>
                        )
                    })}
                </div>
            )}

            {!hasData && (
                <div className='deep-analysis-section'>
                    <p className='deep-analysis-empty'>
                        Geen data gevonden{sector ? ` voor de sector "${getSectorInfo(sector).label}"` : ''}.
                    </p>
                </div>
            )}

            {hasData && (
                <>
                    <div className='deep-analysis-section'>
                        <div className='deep-analysis-section-header'>
                            <h3>Dataset</h3>
                            <p>
                                {summary.effectCount} effecten · {summary.itemCount} items ·{' '}
                                {summary.orgCount} organisaties · {summary.respondentCount} respondenten.
                                Respondenten zijn gepseudonimiseerd (R1…Rn).
                            </p>
                        </div>
                        <div className='msi-research-data-actions'>
                            <div
                                className='msi-research-data-link'
                                onClick={() => exportMsiResultsXlsx({ alphaRows, factors, columns: dataset.columns, rows: dataset.rows })}
                            >
                                <p>Download resultaten (xlsx) ↓</p>
                            </div>
                        </div>
                    </div>

                    <div className='deep-analysis-section'>
                        <div className='deep-analysis-section-header'>
                            <h3>Cronbach&#39;s alpha per effect</h3>
                            <p>
                                Interne consistentie van de items per effect (listwise: alleen
                                respondenten met een score op elk item tellen mee). Een streepje
                                betekent: niet berekenbaar (minder dan 2 items of 2 complete respondenten).
                                Klik op een effect om de onderliggende data te bekijken.
                            </p>
                        </div>
                        <table className='msi-research-data-table'>
                            <thead>
                                <tr>
                                    <th>Effect</th>
                                    <th>Items</th>
                                    <th>Respondenten (compleet)</th>
                                    <th>Alpha</th>
                                </tr>
                            </thead>
                            <tbody>
                                {alphaRows.map((row) => (
                                    <tr
                                        key={row.msiId}
                                        className='msi-research-data-row--clickable'
                                        title='Bekijk de onderliggende data van dit effect'
                                        onClick={() => openDataTable({ effect: row.msiId })}
                                    >
                                        <td>{row.name}</td>
                                        <td>{row.k}</td>
                                        <td>{row.n}</td>
                                        <td>{formatStat(row.alpha)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className='deep-analysis-section'>
                        <div className='deep-analysis-section-header'>
                            <h3>Factoranalyse</h3>
                            <p>
                                Exploratieve factoranalyse over alle items (PCA-extractie,
                                varimax-rotatie, pairwise correlaties). Aantal factoren volgens
                                het Kaiser-criterium (eigenwaarde &gt; 1). Ladingen ≥ {LOADING_HIGHLIGHT} zijn gemarkeerd.
                                Klik op een item om de onderliggende data te bekijken.
                            </p>
                        </div>
                        {!factors && (
                            <p className='deep-analysis-empty'>
                                Te weinig data voor een factoranalyse (minimaal 2 items en 3 respondent-rijen).
                            </p>
                        )}
                        {factors && (
                            <>
                                <p className='msi-research-data-eigenvalues'>
                                    Eigenwaarden:{' '}
                                    {factors.eigenvalues.slice(0, Math.max(factors.numFactors + 2, 4)).map((value, index) => (
                                        <span key={index}>
                                            {index > 0 && ' · '}
                                            {value.toFixed(2)} ({Math.round(factors.explained[index] * 100)}%)
                                        </span>
                                    ))}
                                </p>
                                <table className='msi-research-data-table'>
                                    <thead>
                                        <tr>
                                            <th>Item</th>
                                            <th>Effect</th>
                                            {Array.from({ length: factors.numFactors }, (_, f) => (
                                                <th key={f}>F{f + 1}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {dataset.columns.map((column, itemIndex) => (
                                            <tr
                                                key={column.key}
                                                className='msi-research-data-row--clickable'
                                                title='Bekijk de onderliggende data van dit item'
                                                onClick={() => openDataTable({ item: column.key })}
                                            >
                                                <td>{column.itemTitle}</td>
                                                <td>{column.effectName}</td>
                                                {factors.loadings[itemIndex].map((loading, f) => (
                                                    <td
                                                        key={f}
                                                        className={Math.abs(loading) >= LOADING_HIGHLIGHT
                                                            ? 'msi-research-data-loading--strong'
                                                            : ''}
                                                    >
                                                        {loading.toFixed(2)}
                                                    </td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </>
                        )}
                    </div>
                </>
            )}
        </>
    )
}

export default MsiResearchDataSection
